import { BarChart3, Users } from "lucide-react";

interface DashboardContentProps {
  agents: any[];
  sales: any[];
  unpaidSales: any[];
  totalToPay: number;
  topAgents: any[];
  agentsWithStats: any[];
}

export default function DashboardContent({
  agents,
  sales,
  unpaidSales,
  totalToPay,
  topAgents,
  agentsWithStats,
}: DashboardContentProps) {
  return (
    <div className="space-y-4 md:space-y-6 p-2 sm:p-4 md:p-6">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-6 w-6" />
        <h1 className="text-2xl md:text-3xl font-bold">Дашборд</h1>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-lg border bg-card p-4 md:p-6">
          <div className="text-3xl md:text-4xl font-bold text-blue-600">{agents.length}</div>
          <div className="text-sm md:text-base text-muted-foreground mt-1">Агентов</div>
        </div>
        <div className="rounded-lg border bg-card p-4 md:p-6">
          <div className="text-3xl md:text-4xl font-bold text-blue-600">{unpaidSales.length}</div>
          <div className="text-sm md:text-base text-muted-foreground mt-1">Невыплаченных продаж</div>
        </div>
        <div className="rounded-lg border bg-card p-4 md:p-6">
          <div className="text-3xl md:text-4xl font-bold text-blue-600">{totalToPay.toLocaleString("ru-RU")} ₽</div>
          <div className="text-sm md:text-base text-muted-foreground mt-1">К выплате</div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold mb-4">ТОП-3 агента (неделя)</h2>
        {topAgents.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">Нет данных за последнюю неделю</div>
        ) : (
          <>
        <div className="block md:hidden space-y-3">
          {topAgents.map((agent) => (
            <div key={agent.id} className="border rounded-lg p-3 space-y-2">
              <div className="font-semibold">{agent.name}</div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Продаж:</span>
                <span>{agent.salesCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Комиссия:</span>
                <span className="font-semibold">{agent.totalCommission.toLocaleString("ru-RU")} ₽</span>
              </div>
              <div>
                <a href={`/admin/agents/${agent.id}`} className="text-blue-600 hover:underline text-sm">Детали</a>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 md:p-3 text-sm md:text-base">Агент</th>
                <th className="text-left p-2 md:p-3 text-sm md:text-base">Продаж</th>
                <th className="text-left p-2 md:p-3 text-sm md:text-base">Комиссия</th>
                <th className="text-left p-2 md:p-3 text-sm md:text-base">Действия</th>
              </tr>
            </thead>
            <tbody>
              {topAgents.map((agent) => (
                <tr key={agent.id} className="border-b">
                  <td className="p-2 md:p-3 text-sm md:text-base">{agent.name}</td>
                  <td className="p-2 md:p-3 text-sm md:text-base">{agent.salesCount}</td>
                  <td className="p-2 md:p-3 text-sm md:text-base font-semibold">{agent.totalCommission.toLocaleString("ru-RU")} ₽</td>
                  <td className="p-2 md:p-3 text-sm md:text-base">
                    <a href={`/admin/agents/${agent.id}`} className="text-blue-600 hover:underline">Детали</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>

      <div className="rounded-lg border bg-card p-4 md:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg md:text-xl font-semibold">Агенты</h2>
          </div>
          <a
            href="/admin/agents"
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm md:text-base whitespace-nowrap text-center"
          >
            + Новый агент
          </a>
        </div>
        <div>
          <h3 className="text-base md:text-lg font-medium mb-4">Список агентов</h3>
          {agentsWithStats.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">Агенты не найдены</div>
          ) : (
            <>
          <div className="block md:hidden space-y-3">
            {agentsWithStats.slice(0, 5).map((agent) => (
              <div key={agent.id} className="border rounded-lg p-3 space-y-2">
                <div className="font-semibold">{agent.code}</div>
                <div className="font-semibold">{agent.name}</div>
                {agent.company && (
                  <div className="text-sm text-muted-foreground">{agent.company}</div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Продаж (не выплачено):</span>
                  <span>{agent.unpaidCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">К выплате:</span>
                  <span className="font-semibold">{agent.unpaidTotal.toLocaleString("ru-RU")} ₽</span>
                </div>
                <div className="flex gap-2 pt-2">
                  <a href={`/admin/agents/${agent.id}`} className="text-blue-600 hover:underline text-sm">Детали</a>
                  <button className="text-blue-600 hover:underline text-sm">Ред.</button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 md:p-3 text-xs md:text-sm">ID</th>
                  <th className="text-left p-2 md:p-3 text-xs md:text-sm">Имя / Компания</th>
                  <th className="text-left p-2 md:p-3 text-xs md:text-sm">Продаж (не выплачено)</th>
                  <th className="text-left p-2 md:p-3 text-xs md:text-sm">К выплате</th>
                  <th className="text-left p-2 md:p-3 text-xs md:text-sm">Действия</th>
                </tr>
              </thead>
              <tbody>
                {agentsWithStats.slice(0, 5).map((agent) => (
                  <tr key={agent.id} className="border-b hover:bg-muted/50">
                    <td className="p-2 md:p-3 text-xs md:text-sm">{agent.code}</td>
                    <td className="p-2 md:p-3 text-xs md:text-sm">
                      <div className="font-semibold">{agent.name}</div>
                      {agent.company && (
                        <div className="text-muted-foreground text-xs">{agent.company}</div>
                      )}
                    </td>
                    <td className="p-2 md:p-3 text-xs md:text-sm">{agent.unpaidCount}</td>
                    <td className="p-2 md:p-3 text-xs md:text-sm font-semibold">{agent.unpaidTotal.toLocaleString("ru-RU")} ₽</td>
                    <td className="p-2 md:p-3 text-xs md:text-sm">
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                        <a href={`/admin/agents/${agent.id}`} className="text-blue-600 hover:underline">Детали</a>
                        <button className="text-blue-600 hover:underline">Ред.</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

